import { AllCreatorsPageData } from '@services/MarppleShop/renderApp/pages/AllCreators/AllCreatorsPage.ts';
import { CoreUiS } from '@services/MarppleShop/features/Core/outbound/share.ts';
import {
  CreatorFilterButton,
  CreatorFilterButtonData,
} from '@services/MarppleShop/renderApp/components/cells/CreatorFilterButton/CreatorFilterButton.ts';
import { map, pipe, toArray, zipWithIndex } from '@fxts/core';
import { CreatorSearchCard } from '@services/MarppleShop/renderApp/components/cells/CreatorSearchCard/CreatorSearchCard.ts';
import {
  Pagination,
  PaginationChangeEvent,
} from '@services/MarppleShop/renderApp/components/cells/Pagination/Pagination.ts';
import { CustomEventWithoutDetail, Html, html, ListView, View } from 'rune-ts';
import klass from './AllCreators.module.scss';
import { CollaborationController } from '@services/MarppleShop/renderApp/components/cells/Collaboration/CollaborationController.ts';
import { Modal, ModalClosedEvent } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { CollaborationModalFactory } from '@services/MarppleShop/renderApp/components/cells/Collaboration/CollaborationModal.ts';
import { BecomeFanButtonController } from '@services/MarppleShop/renderApp/components/cells/CreatorMainProfile/controllers/BecomeFanController.ts';
import { updateUrlParams } from '@services/MarppleShop/shared/util';
import { CreatorApiS } from '@services/MarppleShop/features/Creator/outbound/share.ts';
import { ConfigSentryF } from '@modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { loading, loadingEnd } from '@services/MarppleShop/shared/client/global';
import { CreatorSearchSectionView } from '@services/MarppleShop/renderApp/pages/AllCreators/views/CreatorSearchSectionView.ts';
import { SearchEmptyScreen } from '@services/MarppleShop/renderApp/pages/SearchResult/views/SearchEmptyScreen.ts';
import { goLogin } from '@services/MarppleShop/shared/util/goLogin.ts';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import { UtilF } from '@modules/Util/F/Function/module/UtilF';

type QueryParams = Pick<AllCreatorsPageData['creator_search'], 'page' | 'st_id' | 'search'>;

type SingleQueryParam =
  | { page: QueryParams['page']; st_id?: never; search?: never }
  | { page?: never; st_id: QueryParams['st_id']; search?: never }
  | { page?: never; st_id?: never; search: QueryParams['search'] };

class CreatorSearchCardListView extends ListView<CreatorSearchCard> {
  override className = klass.content;
  ItemView = CreatorSearchCard;
}

class CreatorFilterButtonListView extends View<{ button_datas: CreatorFilterButtonData[] }> {
  override className = klass.filter;
  private itemViews: CreatorFilterButton[];
  static FIRST_ROW_MAX_COUNT = 5;
  static FULL_COUNT = 20;
  static DIVIDE_COUNT = 13;

  constructor(
    data: { button_datas: CreatorFilterButtonData[] },
    private readonly is_mobile: boolean,
  ) {
    super(data);

    this.itemViews = this.makeFilterButtonViews();
  }

  override template(): Html {
    if (this.is_mobile) {
      const [first_row, second_row] = this.makeMobileItemViewRows();

      return html`
        <div class="${klass.filter}">
          <div class="${klass.row}">${first_row}</div>
          ${second_row ? html`<div class="${klass.row}">${second_row}</div>` : ''}
        </div>
      `;
    } else {
      return html`<div class="${klass.filter}">${this.itemViews}</div> `;
    }
  }

  private makeMobileItemViewRows() {
    if (this.itemViews.length < CreatorFilterButtonListView.FIRST_ROW_MAX_COUNT) {
      return [this.itemViews, undefined] as const;
    }

    const standard =
      this.itemViews.length > CreatorFilterButtonListView.FULL_COUNT
        ? CreatorFilterButtonListView.DIVIDE_COUNT
        : Math.ceil(this.itemViews.length / 2);

    const first_row = this.itemViews.slice(0, standard);
    const second_row = this.itemViews.slice(standard, this.itemViews.length);

    return [first_row, second_row] as const;
  }

  private makeFilterButtonViews() {
    return this.data.button_datas.map((button_data) => new CreatorFilterButton(button_data));
  }

  public setDataAndRedraw(button_datas: CreatorFilterButtonData[]) {
    this.data.button_datas = button_datas;
    this.itemViews = this.makeFilterButtonViews();
    this.redraw();
  }
}

class AllCreatorsPageReplaceUrlEvent extends CustomEventWithoutDetail {}

export class AllCreatorsPageController {
  static SEARCH_CARD_LAZY_LOAD_COUNT = 5;

  public titleView: SectionTitle;

  public creatorFilterButtonListView: CreatorFilterButtonListView;

  public creatorSearchCardListView: CreatorSearchCardListView;

  public paginationView: Pagination;

  private collaborationModalFactory: CollaborationModalFactory;

  public collaborationController: CollaborationController;

  public collaborationModalView: Modal;

  public creatorSearchSectionView: CreatorSearchSectionView;

  public creatorSearchModal: Modal;

  public searchEmptyScreen: SearchEmptyScreen;

  public static ReplaceUrlEvent = AllCreatorsPageReplaceUrlEvent;

  constructor(private readonly data: AllCreatorsPageData) {
    this.titleView = CoreUiS.makeCreatorRankingTitle(this.data.layout_options.is_mobile, 'creator_search');

    this.creatorFilterButtonListView = new CreatorFilterButtonListView(
      {
        button_datas: this.makeCreatorFilterButtonDatas(this.data.filters, this.data.creator_search.st_id),
      },
      data.layout_options.is_mobile,
    );

    this.creatorSearchCardListView = new CreatorSearchCardListView(
      this.makeCreatorSearchCardDatas(this.data.creators, this.data.layout_options.is_mobile),
    );

    this.paginationView = new Pagination({
      page: this.data.creator_search.page || 1,
      size: this.data.layout_options.is_mobile ? 5 : 10,
      total: Pagination.calcTotal({
        total_count: this.data.search_count,
        limit_per_page: this.data.creator_search.limit,
      }),
    });

    this.creatorSearchSectionView = new CreatorSearchSectionView(
      {
        count: this.data.all_count,
        value: this.data.creator_search.search || '',
      },
      { is_mobile: this.data.layout_options.is_mobile, highlight: 'fill' },
    );

    this.collaborationController = new CollaborationController({
      creator_name: '',
      is_mobile: this.data.layout_options.is_mobile,
    });

    this.collaborationModalFactory = new CollaborationModalFactory({
      collaborationController: this.collaborationController,
    });

    this.collaborationModalView = this.collaborationModalFactory.getModal();

    this.creatorSearchModal = new Modal({
      size: 'large',
      body: this.creatorSearchSectionView,
      isOpen: false,
      full_width: true,
    });

    this.searchEmptyScreen = new SearchEmptyScreen({
      klass: `${klass.empty} ${this.data.creators.length ? klass.hide : ''}`,
      image_src:
        '//s3.marpple.co/files/u_4648126/2025/1/original/9a271e0fda187aacfea75723bf91758211ab66a337.webp',
    });
  }

  public delegateProposalCollaboration(view: View) {
    view.addEventListener(CreatorSearchCard.ProposalButtonClickEvent, (e) => {
      const { name } = e.detail;
      this.collaborationModalView.open();
      this.collaborationController.setCreatorName(name || '');
      this.collaborationModalView.scrollBodyTo(0, 0);
    });

    view.addEventListener(ModalClosedEvent, () => {
      this.collaborationController.resetFormData();
    });

    this.collaborationController.delegateFormChangeEvent(
      this.collaborationModalView,
      this.collaborationController.changeSubmitButtonByValidation,
    );

    this.collaborationController.delegateSubmitEvent(this.collaborationModalView, () => {
      this.collaborationModalView.close();
    });

    this.collaborationController.delegateCancelEvent(this.collaborationModalView, () => {
      this.collaborationModalView.close();
    });
  }

  public delegateFanLetterButtonClick(view: View) {
    view.delegate(CreatorSearchCard.FanButtonClickEvent, CreatorSearchCard, async (e, Card) => {
      if (this.data.layout_options.user?.id) {
        const { id, is_liked } = e.detail;

        Card.setLikeState(!!is_liked);

        BecomeFanButtonController.postLike({ is_liked: !!is_liked, store_id: id }, () => {
          Card.setLikeState(!is_liked);
        });
      } else {
        await goLogin();
      }
    });
  }

  public delegateCreatorFilterButtonClick(view: View, filters: AllCreatorsPageData['filters']) {
    view.delegate(CreatorFilterButton.ClickEvent, CreatorFilterButton, (e, view) => {
      const { id } = e.detail;

      this.replaceUrl(view, { st_id: id });

      this.creatorFilterButtonListView.setDataAndRedraw(this.makeCreatorFilterButtonDatas(filters, id));
      view.redraw();
    });
  }

  public delegatePaginationChangeEvent(view: View) {
    view.delegate(PaginationChangeEvent, Pagination, (e) => {
      const { page } = e.detail;

      this.replaceUrl(view, { page });
    });
  }

  public delegateReplaceUrlEvent(view: View) {
    view.addEventListener(AllCreatorsPageController.ReplaceUrlEvent, async () => {
      try {
        const { st_id, limit } = this.data.creator_search;
        this.paginationView.disableClick();

        loading();

        const { creators, search_count } = await CreatorApiS.getCreatorSearch(this.data.creator_search);

        this.data.creators = creators;
        this.data.search_count = search_count.count;

        this.creatorFilterButtonListView.setDataAndRedraw(
          this.makeCreatorFilterButtonDatas(this.data.filters, st_id),
        );

        this.creatorSearchCardListView.set(
          this.makeCreatorSearchCardDatas(this.data.creators, this.data.layout_options.is_mobile),
        );

        this.paginationView.setTotal(
          Pagination.calcTotal({
            total_count: search_count.count,
            limit_per_page: limit,
          }),
        );
        this.paginationView.redraw();

        this.searchEmptyScreen.redraw();

        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        UtilF.refreshLazyLoad();
      } catch (e) {
        ConfigSentryF.error(e);
      } finally {
        loadingEnd();
        this.paginationView.enableClick();
      }
    });
  }

  private replaceUrl(view: View, query_params: SingleQueryParam) {
    this.setQueryParams(query_params);
    history.replaceState(null, '', updateUrlParams(location.href, this.queryParams));
    view.dispatchEvent(AllCreatorsPageController.ReplaceUrlEvent, {
      bubbles: true,
    });
  }

  private setQueryParams(query_params: SingleQueryParam) {
    const map = new Map(Object.entries(query_params));

    if (map.has('page')) {
      this.data.creator_search = {
        ...this.data.creator_search,
        page: query_params.page || 1,
      };
    }

    if (map.has('st_id')) {
      this.data.creator_search.st_id = query_params.st_id;
      this.data.creator_search.page = 1;
    }

    if (map.has('search')) {
      this.data.creator_search.search = query_params.search;
      this.data.creator_search.st_id = undefined;
      this.data.creator_search.page = 1;
    }
  }

  private get queryParams(): QueryParams {
    return {
      page: this.data.creator_search.page,
      search: this.data.creator_search.search,
      st_id: this.data.creator_search.st_id,
    };
  }

  private makeCreatorFilterButtonDatas(
    filters: AllCreatorsPageData['filters'],
    st_id?: AllCreatorsPageData['creator_search']['st_id'],
  ) {
    const filter_button_datas = pipe(
      filters,
      map((filter) => ({
        ...filter,
        selected: filter.id === st_id,
      })),
      toArray,
    );

    return filter_button_datas.length
      ? [
          {
            id: undefined,
            name: ET('common::all'),
            selected: !st_id,
          },
          ...filter_button_datas,
        ]
      : [];
  }

  private makeCreatorSearchCardDatas(creators: AllCreatorsPageData['creators'], is_mobile: boolean) {
    return pipe(
      creators,
      zipWithIndex,
      map(([index, creator]) => ({
        creator,
        is_mobile,
        is_lazy: AllCreatorsPageController.SEARCH_CARD_LAZY_LOAD_COUNT < index,
      })),
      toArray,
    );
  }

  public get texts() {
    return {
      search_result: ET('mps2::creator_search::search_result', {
        search_count: this.data.search_count,
        klass: klass.blue,
      }),
    };
  }
}
